import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import Banner from "../../../DesignSystem/Banner/Banner";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import { useAdviserCharges } from "../context/AdviserChargesContext";
import { useAdviserList } from "../../../../context/Adviser/Utils/AdviserEmailList";
import withMonthlyPaymentsAccess from "../components/withMonthlyPaymentsAccess";
import AddPayment from "./AddPayment";
import { subscribeMonthlyManualPayments } from "../services/manualPaymentService";
import { lastWorkingDayOfMonth } from "../utils/chargeDates";
import "../../../../css/Finance/MonthlyPayments.css";

const keyOf = (value) => String(value || "").trim().toLowerCase();

const AdviserPaymentPage = () => {
  const navigate = useNavigate();
  const { adviserEmail: routeAdviser } = useParams();
  const [searchParams] = useSearchParams();
  const { selectedMonth, setSelectedMonth, monthOptions, chargeRecords, advisersByEmail, selectedMonthClosed } = useAdviserCharges();
  const { advisers } = useAdviserList();
  const email = keyOf(routeAdviser);
  const requestedMonth = searchParams.get("month");
  const sourceView = searchParams.get("view") === "advances" ? "advances" : "additional-payments";
  const paymentType = sourceView === "advances" ? "advance" : "additional_payment";
  const sourceViewLabel = sourceView === "advances" ? "Advances" : "Additional Payments";
  const paymentId = searchParams.get("payment");
  const [payments, setPayments] = useState([]);
  useEffect(() => subscribeMonthlyManualPayments(setPayments, () => setPayments([])), []);
  const payment = payments.find((item) => item.id === paymentId);
  useEffect(() => {
    if (
      monthOptions.some((option) => option.value === requestedMonth) &&
      selectedMonth !== requestedMonth
    ) setSelectedMonth(requestedMonth);
  }, [monthOptions, requestedMonth, selectedMonth, setSelectedMonth]);
  const adviser = useMemo(() => {
    const fromList = advisers.find((item) => keyOf(item.email || item.id) === email);
    const fromCharges = advisersByEmail[email]?.adviser;
    const record = chargeRecords.find((row) => keyOf(row.adviserEmail) === email);
    return {
      name: fromList?.name || fromCharges?.adviser || record?.adviser || routeAdviser,
      brand: fromList?.brandName || fromCharges?.brandName || fromCharges?.practice || "-",
      email: fromList?.email || fromCharges?.adviserEmail || email || routeAdviser,
      id: fromList?.adviserId || fromCharges?.adviserId || "",
    };
  }, [advisers, advisersByEmail, chargeRecords, email, routeAdviser]);
  const breakdownUrl = `${APP_ROUTES.CHARGES_BREAKDOWN}/${encodeURIComponent(adviser.email)}?month=${selectedMonth}&view=${sourceView}`;
  const chargesUrl = `${APP_ROUTES.CHARGES_BREAKDOWN}?month=${selectedMonth}&tab=${sourceView}`;
  return (
    <div className="Main-Page">
      <Helmet><title>Add Payment</title></Helmet>
      <Sidebar isSetExpanded={true} />
      <main className="Main-Container monthly-payments-page table-contained-page column gap-m">
        <Breadcrumb items={[
          { label: "Finance", to: "/Department/Finance" },
          { label: "Monthly Payments", to: APP_ROUTES.CHARGES_SUMMARY_DASHBAORD },
          // { label: "Charges Breakdown", to: chargesUrl },
          { label: sourceViewLabel, to: chargesUrl },
          { label: adviser.name, to: breakdownUrl },
          { label: "Add Payment" },
        ]} />
        <h3 className="FP_H3">{paymentId ? "Edit Payment" : "Add Advances/Additional Payments"}</h3>
        {selectedMonthClosed ? (
          <Banner
            type="info"
            message="This month is closed. Its payments can no longer be added, edited, or removed."
          />
        ) : (
          <AddPayment key={payment ? payment.id : `loading-${paymentId || "new"}`} adviser={adviser} payment={payment} initialType={paymentType} defaultDate={lastWorkingDayOfMonth(requestedMonth || selectedMonth)} onClose={(type) => navigate(`${APP_ROUTES.CHARGES_BREAKDOWN}/${encodeURIComponent(adviser.email)}?month=${selectedMonth}&view=${type === "advance" ? "advances" : "additional-payments"}`)} asPage />
        )}
      </main>
    </div>
  );
};

export default withMonthlyPaymentsAccess(AdviserPaymentPage);
